import { Component, ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import AppHeader, { Application, ForstaLogoAppearance } from '@jotunheim/react-app-header';
import { HistoryContext } from '@jotunheim/react-contexts';
import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { LoggingMessageService } from '../../services/logging-message.service';
import { GlobalHeaderMenuService } from '../../services/global-header-menu.service';

const containerElementName = 'pgglobalheader';

@Component({
  selector: 'shared-pg-global-header',
  standalone: true,
  template: `<span #${containerElementName}></span>`,
})
// tslint:disable:cyclomatic-complexity
export class PgGlobalHeaderComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
  @ViewChild(containerElementName, { static: false }) public element: ElementRef;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private applicationName = 'NDNQI';
  private reactRoot: Root | null = null;
  private menuPromise: Promise<any> | null = null;

  @Input() menuLoading: boolean;
  @Input() isUserLoggedIn: boolean;
  @Input() isFederationLoaded: boolean;
  @Input() userEmail: string;
  @Output() logoutClicked: EventEmitter<any> = new EventEmitter();

  constructor(
    private authService: AuthService,
    private angularrouter: Router,
    private logger: LoggingMessageService,
    private globalHeaderMenu: GlobalHeaderMenuService,
  ) {
    // this.angularrouter.events.pipe(
    //     filter((event): boolean => event instanceof NavigationEnd),
    //     takeUntil(this.ngUnsubscribe))
    //   .subscribe((event: NavigationEnd): void => {
    //     if (!this.authService.isUserLoggedIn()) return;
    //     // Only render if we haven't mounted yet
    //     if (!this.reactRoot) {
    //       this.render();
    //     }
    //   });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Only re-render if inputs that affect header actually changed
    if (changes['isUserLoggedIn'] || changes['userEmail']) {
      this.render();
    }
  }

  public ngOnInit() {
  }

  public ngAfterViewInit() {
    this.render();
  }

  public ngOnDestroy() {
    //console.log('jotunheim: [PgGlobalHeader] ngOnDestroy');
    if (this.reactRoot) {
      //console.log('jotunheim: [PgGlobalHeader] unmounting react root');
      this.reactRoot.unmount();
      this.reactRoot = null;
    }

    // Ensure any pending references are released
    this.menuPromise = null;
    // Clear GlobalNav cached values owned by the menu service
    this.globalHeaderMenu.clearGlobalNavUser();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public render() {
    try {
      if (!this.element || !this.element.nativeElement) {
        this.logger.info('pg-global-header', 'render', 0, '', 'bail:no-element', '');
        return;
      }

      const isLoggedIn = this.authService.isUserLoggedIn();
      if (!isLoggedIn) {
        this.logger.info('pg-global-header', 'render', 0, '', 'bail:not-logged-in', '');
        return;
      }

      const email = this.userEmail || this.authService.getTokenEmail();
      if (!email) {
        this.logger.info('pg-global-header', 'render', 0, '', 'bail:no-email', '');
        return;
      }

      // Prepare apps container; build user after GN payload resolves
      //const apps: Array<Application> = [{ id: 'home', name: 'Home', links: { self: '/hx-dashboard' }, isExternal: false, current: this.applicationName === 'Home' }];
      const apps: Array<Application> = [];

      if (!this.menuPromise) {
        this.menuPromise = firstValueFrom(this.globalHeaderMenu.fetchGlobalNavUserInfo());
      }

      this.menuPromise.then(gnResp => {
        try {
          // Build user after GN payload is available to avoid empty names on initial render
          const gnFirst = (gnResp && (gnResp.firstname || (gnResp as any)?.body?.firstname)) || this.globalHeaderMenu.getGlobalNavFirstName();
          const gnLast = (gnResp && (gnResp.lastname || (gnResp as any)?.body?.lastname)) || this.globalHeaderMenu.getGlobalNavLastName();
          const first = (gnFirst || '').trim();
          const last = (gnLast || '').trim();
          const displayName = first || email;
          const user = {
            userName: displayName,
            firstName: first,
            lastName: last,
            // email: email, // uncomemnting this will show the email instead of the displayName
            companyName: email,
            companyId: ''
          };

          const menu = (gnResp && (gnResp.navigation || (gnResp as any)?.body?.navigation || (gnResp as any)?.data?.navigation)) || [];
          if (!Array.isArray(menu)) {
            this.logger.error('pg-global-header', 'menu', 0, '', 'invalid-navigation-payload', JSON.stringify(gnResp || {}));
            return;
          }

          let apptct = 1;
          menu.forEach(item => {
            try {
              const link = item.externalLink || (item.href ? '/' + item.href : '');
              apps.push({
                id: apptct.toString(),
                name: item.name,
                links: { self: link },
                target: item.externalLink ? (item.OpenInSameWindow ? '_top' : '_new') : '',
                isExternal: !!item.externalLink,
                current: false
              });
              apptct++;
            } catch (e) {
              this.logger.error('pg-global-header', 'menu', 0, '', 'menu-item-build-failed', JSON.stringify({ item, error: (e as any)?.message || e }));
            }
          });

          const history = {
            push: (_url: any) => {
              // no-op because we’re not using internal links. Below code is for future purpose in case we need to add internal links.
            }
          };

          //// This history is provided to <AppHeader> via HistoryContext.Provider. 
          //// When the user clicks an internal app link (non-external), the header calls history.push(...) to route within the Angular app.
          //// Since we are not adding local links to globalNav, No need to build history. 

          // const angularrouter = this.angularrouter;
          // const basePrefix = (window.location && window.location.pathname && window.location.pathname.startsWith('/ndnqiwrapper')) ? '/ndnqiwrapper' : '';
          // const history = {
          //   push: (url) => {
          //    // this.logger.info('pg-global-header', 'history', 0, '', `push called for ${url?.pathname || ''}`, '');
          //     try {
          //       const pathname = (url && url.pathname) || '';
          //       if (!pathname || pathname === window.location.pathname) return; // no-op
          //       let fullPath = `${pathname}${url.search || ''}${url.hash || ''}`;
          //       if (!fullPath.startsWith('/')) fullPath = `/${fullPath}`;
          //       if (basePrefix && !fullPath.startsWith(basePrefix)) {
          //         fullPath = `${basePrefix}${fullPath}`;
          //       }
          //       angularrouter.navigateByUrl(fullPath);
          //     } catch (e) {
          //       this.logger.error('pg-global-header', 'history', 0, '', 'navigateByUrl-failed', (e as any)?.message || '');
          //     }
          //   }
          // };

          try {
            if (!this.reactRoot) {
              this.reactRoot = createRoot(this.element.nativeElement);
            }
            this.reactRoot.render(
              <span style={{ position: 'fixed', width: '100%', zIndex: 1000, top: '0px' }}>
                <HistoryContext.Provider value={history}>
                  <AppHeader title={this.applicationName} oidcEnabled={false} showSearch={false} logoAppearance={ForstaLogoAppearance.PressGaney_HX}
                    showRecentItems={false} showRecentMenuDropDown={false}
                    showUserMenu={true} user={user}
                    //userSettingsLink={{href: '/user-preferences', text: 'My Preferences', isExternal: false, target: '_self'}}
                    //showFeedbackButton={true} feedbackLink={{ href: 'https://hireply.io/z2HLQK' }}
                    showHelpMenu={false}
                    applications={apps} showUserInfo={true}
                    changePasswordLink={null}
                    onLogoutClick={event => {
                      event.preventDefault();
                      // Clear GN cache before logging out so header does not show stale state
                      this.globalHeaderMenu.clearGlobalNavUser();
                      this.logoutClicked.emit();
                      this.authService.logout();
                    }}
                    showGlobalNavigationBackdrop={true}
                    //allItemsHref={appurl}
                  >
                  </AppHeader>
                </HistoryContext.Provider>
              </span>
            );
          } catch (e) {
            this.logger.error('pg-global-header', 'render', 0, '', 'react-render-failed', (e as any)?.message || '');
          }
        } catch (inner) {
          this.logger.error('pg-global-header', 'menu', 0, '', 'menu-then-handler-failed', (inner as any)?.message || '');
        }
      }).catch(ex => {
        // already present in your file; ensure error-level log
        this.logger.error('pg-global-header', 'menu', 0, '', 'load failed - forcing logout', (ex as any)?.message || '');
        this.authService.logout();
      });

    } catch (err) {
      this.logger.error('pg-global-header', 'render', 0, '', 'unexpected-exception', (err as any)?.message || '');
    }
  }
} 
